import { errorMessageFa } from '@saay/api-client';
import { normalizeDigits } from '@saay/formatting';

import { buttonVariants } from '@saay/shadcn/components/button';
import { NativeSelect, SearchField } from '../kit.js';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { OPENED_FROM_LIST } from './route-sheet.js';
import { useCategories, useCities, useProvinces } from '../queries.js';

/**
 * Geography and category pickers (AC §17.5.1). Only `ACTIVE` rows are offered: the geography lists
 * return only active rows, and inactive categories are filtered out. A failed list shows its
 * `messageFa` on the field so the form stays usable.
 */

export interface GeographyFieldsProps {
  readonly cityError?: string | undefined;
  readonly cityId: string;
  readonly idPrefix: string;
  readonly onCityChange: (cityId: string) => void;
  readonly onProvinceChange: (provinceId: string) => void;
  readonly provinceError?: string | undefined;
  readonly provinceId: string;
}

export function GeographyFields({
  cityError,
  cityId,
  idPrefix,
  onCityChange,
  onProvinceChange,
  provinceError,
  provinceId,
}: GeographyFieldsProps) {
  const provinces = useProvinces();
  const cities = useCities(provinceId === '' ? undefined : provinceId);

  useEffect(() => {
    if (provinces.status !== 'success' || provinces.data === undefined || provinceId !== '') {
      return;
    }
    if (provinces.data.length === 1) {
      onProvinceChange(provinces.data[0]!.id);
    }
  }, [onProvinceChange, provinceId, provinces.data, provinces.status]);

  useEffect(() => {
    if (cities.status !== 'success' || cities.data === undefined || cityId !== '') {
      return;
    }
    if (cities.data.length === 1) {
      onCityChange(cities.data[0]!.id);
    }
  }, [cities.data, cities.status, cityId, onCityChange]);

  const provinceHint =
    provinces.status === 'pending'
      ? 'در حال بارگذاری استان‌ها…'
      : provinces.status === 'error'
        ? errorMessageFa(provinces.error)
        : undefined;
  const cityHint =
    provinceId === ''
      ? 'اول استان را انتخاب کنید.'
      : cities.status === 'pending'
        ? 'در حال بارگذاری شهرها…'
        : cities.status === 'error'
          ? errorMessageFa(cities.error)
          : undefined;

  return (
    <div className="admin-grid-2">
      <NativeSelect
        error={provinceError}
        hint={provinceHint}
        id={`${idPrefix}-province`}
        label="استان"
        onChange={(event) => {
          onProvinceChange(event.target.value);
          onCityChange('');
        }}
        options={(provinces.data ?? []).map((item) => ({ label: item.nameFa, value: item.id }))}
        placeholder="انتخاب استان"
        value={provinceId}
      />
      <NativeSelect
        disabled={provinceId === '' || cities.status !== 'success'}
        error={cityError}
        hint={cityHint}
        id={`${idPrefix}-city`}
        label="شهر"
        onChange={(event) => onCityChange(event.target.value)}
        options={(cities.data ?? []).map((item) => ({ label: item.nameFa, value: item.id }))}
        placeholder="انتخاب شهر"
        value={cityId}
      />
    </div>
  );
}

export interface CategoryPickerProps {
  readonly error?: string | undefined;
  readonly hint?: string;
  readonly id: string;
  readonly label?: string;
  readonly onChange: (categoryId: string) => void;
  /** Label of the empty option (for example «همه» in a filter). */
  readonly placeholder?: string;
  /** Registration (ADM-13): filter by path and offer «+ دسته‌بندی جدید». */
  readonly registration?: boolean;
  readonly value: string;
}

function categoryMatches(pathFa: string, query: string): boolean {
  const needle = normalizeDigits(query.trim()).toLowerCase();
  if (needle === '') return true;
  return normalizeDigits(pathFa).toLowerCase().includes(needle);
}

/** Active categories by their full path (`pathFa`), sorted as the API returns them. */
export function CategoryPicker({
  error,
  hint,
  id,
  label = 'دسته‌بندی اصلی',
  onChange,
  placeholder = 'انتخاب دسته‌بندی',
  registration = false,
  value,
}: CategoryPickerProps) {
  const categories = useCategories('ACTIVE');
  const [query, setQuery] = useState('');
  const status =
    categories.status === 'pending'
      ? 'در حال بارگذاری دسته‌بندی‌ها…'
      : categories.status === 'error'
        ? errorMessageFa(categories.error)
        : undefined;
  const options = useMemo(() => {
    const rows = categories.data ?? [];
    return rows
      .filter((item) => !registration || categoryMatches(item.pathFa, query))
      .map((item) => ({ label: item.pathFa, value: item.id }));
  }, [categories.data, query, registration]);

  return (
    <div className="admin-category-picker">
      {registration ? (
        <SearchField
          label="جست‌وجوی دسته‌بندی"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="نام دسته"
          value={query}
        />
      ) : null}
      <NativeSelect
        error={error}
        hint={status ?? hint}
        id={id}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        options={options}
        placeholder={placeholder}
        value={value}
      />
      {registration ? (
        <Link
          className={buttonVariants({ size: 'sm', variant: 'secondary' })}
          state={OPENED_FROM_LIST}
          to="/merchant-categories"
        >
          + دسته‌بندی جدید
        </Link>
      ) : null}
    </div>
  );
}
